// Accounts in the IDE (Phase 3 of docs/ONLINE_IDE_FEASIBILITY.md): signing in
// with a passkey, projects saved to the account and kept in step with it,
// share links for projects with several files, and shader translation. It
// needs the server (cloud.js); without one, none of it shows.
//
// A project saved to the account keeps, in its details, the account's copy
// it last matched: { id, revision, synced: { path: hash }, name, main, mode }.
// Changes are saved to the account soon after they are made. Opening the
// project, or coming back to the page, brings in changes saved on another
// computer; when both changed, the IDE asks which to keep, and keeps the
// other as a copy.

import { CloudError, hashBlob } from "./cloud.js";
import { setupMembership } from "./membership.js";

const SHADER_SOURCE = /^\s*source\s+"([^"]+)"/im;

export function setupAccount(ide) {
  const { cloud, store, $, notice, clearNotices, status, write } = ide;
  const signedIn = () => !!(cloud && cloud.user);
  // While accounts are closed, only a join link (?join=) makes one
  // (docs/MEMBERSHIP_PLANS_DESIGN.md, "Accounts").
  let joinCode = new URLSearchParams(location.search).get("join");
  const canCreate = () => !!(cloud && (cloud.config.registration !== "closed" || joinCode));
  let syncTimer = 0, syncing = null, pendingSync = false;

  // The account button, the save button beside the project's name, and the
  // account dialog.
  const refresh = () => {
    const project = ide.project();
    $("account-button").hidden = !cloud || !cloud.canUsePasskeys;
    $("account-button").textContent = signedIn() ? cloud.user.name : "Sign in";
    $("account-button").title = signedIn() ? "Your account" : "Sign in to keep your programs in your account";
    const linked = !!(project && project.meta.cloud);
    $("save-to-account").hidden = !signedIn() || !project || linked;
    $("menu-save-to-account").hidden = !signedIn() || !project || linked;
    const chip = $("cloud-status");
    chip.hidden = !cloud || !project || (!linked && !signedIn());
    if (!chip.hidden && !chip.dataset.busy) {
      chip.textContent = linked ? (signedIn() ? "Saved to your account" : "In your account (signed out)") : "In this browser only";
    }
    if (ide.accountChanged) ide.accountChanged();
  };
  const chipText = (text, busy = false) => {
    const chip = $("cloud-status");
    chip.textContent = text;
    if (busy) chip.dataset.busy = "1";
    else delete chip.dataset.busy;
  };

  const showDialog = () => {
    const signedInNow = signedIn();
    $("account-signed-out").hidden = signedInNow;
    $("account-signed-in").hidden = !signedInNow;
    $("account-new").hidden = !canCreate();
    $("account-closed").hidden = canCreate();
    // Membership: what the account has, and buying (membership.js).
    membership.render(signedInNow).catch(() => {});
    if (signedInNow) {
      $("account-name").textContent = cloud.user.name;
      $("account-passkeys").textContent = `${cloud.user.passkeys} passkey${cloud.user.passkeys === 1 ? "" : "s"}`;
    }
    $("account-error").textContent = "";
    $("account-dialog").showModal();
    // The shared library's sections (library.js).
    if (ide.accountShown) ide.accountShown();
  };
  const ceremony = (action) => async () => {
    $("account-error").textContent = "";
    try {
      await action();
      $("account-dialog").close();
      refresh();
      await afterSignIn();
    } catch (error) {
      // Cancelling the browser's passkey prompt is not an error to show.
      if (error && error.name === "NotAllowedError") return;
      $("account-error").textContent = error.message || String(error);
    }
  };
  const membership = setupMembership({ cloud, $, status, notice, showAccount: () => showDialog(),
    setJoinCode: (code) => { joinCode = code; } });
  if (cloud) {
    $("account-button").addEventListener("click", showDialog);
    $("sign-in").addEventListener("click", ceremony(async () => {
      await cloud.signIn();
      status(`Signed in as ${cloud.user.name}.`);
    }));
    $("create-account").addEventListener("click", ceremony(async () => {
      const name = $("account-new-name").value.trim();
      if (!name) throw new Error("Enter a name for the account.");
      await cloud.register(name, joinCode);
      status(`Made the account ${cloud.user.name}. Its passkey is on this device.`);
    }));
    $("add-passkey").addEventListener("click", ceremony(async () => {
      await cloud.addPasskey();
      status("Added a passkey to your account.");
    }));
    $("sign-out").addEventListener("click", async () => {
      await cloud.signOut();
      $("account-dialog").close();
      refresh();
      status("Signed out. Your programs stay in this browser.");
    });
    $("close-account").addEventListener("click", () => $("account-dialog").close());
    // A join link opens the dialog, ready to make the account; so does
    // coming back from a purchase (membership.js).
    if (joinCode && !signedIn() && cloud.canUsePasskeys) showDialog();
    membership.handleReturn();
    const save = () => saveToAccount(ide.project());
    $("save-to-account").addEventListener("click", save);
    $("menu-save-to-account").addEventListener("click", () => { ide.closeMenu(); save(); });
    // Back on the page, changes from another computer come in.
    addEventListener("focus", () => checkRemote(ide.project()));
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") checkRemote(ide.project());
    });
    // And now and then while the page is in view, for two windows open at
    // once.
    setInterval(() => {
      if (document.visibilityState === "visible") checkRemote(ide.project());
    }, 30000);
  }

  async function afterSignIn() {
    const project = ide.project();
    if (project && project.meta.cloud) await checkRemote(project);
    refresh();
  }

  // The project's files and details as the account would hold them.
  async function localState(project) {
    const files = {};
    for (const path of project.paths()) files[path] = await hashBlob(project.blob(path));
    return { files, name: project.name, main: project.main, mode: project.meta.mode || "extended" };
  }
  const sameFiles = (a, b) => {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
  };
  async function changedSinceSync(project) {
    const linked = project.meta.cloud;
    const local = await localState(project);
    return !sameFiles(local.files, linked.synced) || local.name !== linked.name || local.main !== linked.main ||
      local.mode !== linked.mode;
  }

  async function saveToAccount(project) {
    if (!project || !signedIn()) return;
    try {
      chipText("Saving to your account…", true);
      const made = await cloud.createProject({ name: project.name, mode: project.meta.mode });
      await project.update({ cloud: { id: made.id, revision: 0, synced: {}, name: null, main: null, mode: null } });
      await sync(project);
      status(`Saved ${project.name} to your account. Changes are saved there as you make them.`);
      // The Save to account button goes.
      refresh();
    } catch (error) {
      chipText("Not saved to your account");
      notice(`${project.name} could not be saved to your account: ${error.message}`, [], "warning");
    }
  }

  // While a project's buddy session is open, the session saves it to the
  // account (web/server/lib/rooms.mjs), and this browser does not.
  const hosting = (project) => !!(project.meta.buddies && project.meta.buddies.host);

  // Saves the project's changes to its account copy, after a moment's pause.
  function changed(project) {
    if (!project || project !== ide.project() || !project.meta.cloud || !signedIn() || hosting(project)) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      syncTimer = 0;
      sync(project);
    }, 1500);
  }

  async function sync(project, { force = false } = {}) {
    clearTimeout(syncTimer);
    syncTimer = 0;
    if (syncing) {
      pendingSync = true;
      return syncing;
    }
    syncing = (async () => {
      const linked = project.meta.cloud;
      if (!linked || !signedIn()) return;
      if (!force && linked.revision > 0 && !(await changedSinceSync(project))) return;
      chipText("Saving to your account…", true);
      try {
        const files = project.paths().map((path) => [path, project.blob(path)]);
        const saved = await cloud.saveProject(linked.id, { name: project.name, main: project.main,
          mode: project.meta.mode || "extended", files, baseRevision: linked.revision });
        project.meta.cloud = { id: linked.id, revision: saved.revision, synced: saved.manifest, name: project.name,
          main: project.main, mode: project.meta.mode || "extended" };
        await store.saveMeta(project.meta);
        chipText("Saved to your account");
      } catch (error) {
        if (error instanceof CloudError && error.status === 409) {
          chipText("Changed on another computer");
          conflict(project, error.body.project);
        } else if (error instanceof CloudError && error.status === 404) {
          await unlinked(project);
        } else {
          chipText("Not saved to your account yet");
          if (!(error instanceof CloudError)) console.warn(error);
          else notice(`Changes to ${project.name} could not be saved to your account: ${error.message}`, [], "warning");
        }
      }
    })();
    try {
      await syncing;
    } finally {
      syncing = null;
      if (pendingSync) {
        pendingSync = false;
        changed(project);
      }
    }
  }

  async function unlinked(project) {
    await project.update({ cloud: null });
    chipText("In this browser only");
    notice(`${project.name} is no longer in your account; it is kept in this browser.`, [], "cloud");
    refresh();
  }

  // Brings in the account's copy of a project, replacing the files here.
  async function pull(project, remote) {
    chipText("Bringing in changes…", true);
    const remoteFiles = remote.files;
    for (const [path, file] of Object.entries(remoteFiles)) {
      if (project.has(path) && await hashBlob(project.blob(path)) === file.hash) continue;
      await project.write(path, await cloud.projectFile(remote.id, file.hash));
    }
    for (const path of project.paths()) if (!remoteFiles[path]) await project.remove(path);
    const synced = Object.fromEntries(Object.entries(remoteFiles).map(([path, file]) => [path, file.hash]));
    await project.update({ name: remote.name, main: remote.main, mode: remote.mode,
      cloud: { id: remote.id, revision: remote.revision, synced, name: remote.name, main: remote.main, mode: remote.mode } });
    chipText("Saved to your account");
  }

  // Whether the account's copy is newer; brings it in when this computer has
  // no changes of its own, and asks when it has.
  async function checkRemote(project) {
    if (!project || !project.meta.cloud || !signedIn() || syncing || hosting(project)) return;
    let remote;
    try {
      remote = await cloud.getProject(project.meta.cloud.id);
    } catch (error) {
      if (error instanceof CloudError && error.status === 404) await unlinked(project);
      return;
    }
    if (project !== ide.project() || remote.revision <= project.meta.cloud.revision) return;
    if (await changedSinceSync(project)) {
      conflict(project, remote);
      return;
    }
    await ide.saveAll();
    await pull(project, remote);
    await ide.reopen(project);
    status(`Brought in the changes to ${project.name} saved on another computer.`);
  }

  function conflict(project, remote) {
    clearNotices("conflict");
    notice(`${project.name} changed on another computer since this one last saved it to your account.`, [
      ["Use the other computer's version", async () => {
        // This computer's version is kept as a project of its own.
        const copy = await store.create({ name: `${project.name} (this computer)`,
          files: project.paths().map((path) => [path, project.blob(path)]), main: project.main, mode: project.meta.mode });
        const latest = await cloud.getProject(project.meta.cloud.id);
        await pull(project, latest);
        await ide.reopen(project);
        status(`Opened the other computer's version. This computer's is kept as ${copy.name}.`);
      }],
      ["Keep this computer's version", async () => {
        const latest = await cloud.getProject(project.meta.cloud.id);
        project.meta.cloud.revision = latest.revision;
        await sync(project, { force: true });
        status(`Saved this computer's version of ${project.name} to your account.`);
      }],
    ], "conflict");
  }

  // The account's projects that are not in this browser, for the projects
  // dialog: [{ id, name, updated }].
  async function remoteOnly(localMetas) {
    if (!signedIn()) return [];
    const here = new Set(localMetas.map((meta) => meta.cloud && meta.cloud.id).filter(Boolean));
    try {
      return (await cloud.listProjects()).filter((remote) => !here.has(remote.id));
    } catch {
      return [];
    }
  }

  // Opens a project from the account in this browser.
  async function openRemote(id) {
    const remote = await cloud.getProject(id);
    const files = [];
    for (const [path, file] of Object.entries(remote.files)) files.push([path, await cloud.projectFile(id, file.hash)]);
    const synced = Object.fromEntries(Object.entries(remote.files).map(([path, file]) => [path, file.hash]));
    const created = await store.create({ name: remote.name, files, main: remote.main, mode: remote.mode });
    await created.update({ name: remote.name, cloud: { id, revision: remote.revision, synced, name: remote.name,
      main: remote.main, mode: remote.mode } });
    await ide.show(created);
    status(`Opened ${remote.name} from your account.`);
  }

  async function removeRemote(id) {
    await cloud.deleteProject(id);
  }

  // A share link for a project of several files: the project is saved to
  // the account, and the link opens a copy of it as it is now.
  async function shareLink(project) {
    if (!cloud) return { error: "A link can hold a program of one source file. Download its files to share them." };
    if (!signedIn()) return { error: "Sign in to share a program with several files or media by a link." };
    if (!project.meta.cloud) await saveToAccount(project);
    else await sync(project);
    if (!project.meta.cloud) return { error: "The project could not be saved to your account." };
    const token = await cloud.share(project.meta.cloud.id);
    return { url: `${location.origin}${location.pathname}#s=${token}` };
  }

  // Opens a share link's copy as a new project in this browser.
  async function openShare(token) {
    if (!cloud) throw new Error("This site has no server, so share links for programs with several files do not work here");
    const shared = await cloud.getShare(token);
    const files = [];
    for (const [path, file] of Object.entries(shared.files)) files.push([path, await cloud.sharedFile(token, file.hash)]);
    const created = await store.create({ name: shared.name, files, main: shared.main, mode: shared.mode });
    await ide.show(created);
    status(`Opened the shared program ${created.name}, a copy that is yours to change.`);
  }

  // Translates the project's shader assets that declare HLSL source and have
  // changed since they were last translated. Returns the number translated,
  // or reports what stops it.
  async function prepareShaders(project) {
    const assets = [];
    for (const path of project.paths()) {
      if (!/\.b3shader$/i.test(path)) continue;
      const text = await project.text(path);
      const match = SHADER_SOURCE.exec(text);
      if (match) assets.push({ path, source: match[1].replace(/\\/g, "/") });
    }
    if (!assets.length) return 0;
    // Only the assets a source file names, by their file name: a folder of
    // samples holds many shaders its programs never load, and each takes a
    // moment to translate. A program that loads another stops with an error
    // that names it.
    const named = new Set();
    for (const path of project.paths()) {
      if (!/\.(bb|bbinc|inc)$/i.test(path)) continue;
      for (const match of (await project.text(path)).matchAll(/"([^"\r\n]*\.b3shader)"/gi)) {
        named.add(match[1].replace(/\\/g, "/").split("/").pop().toLowerCase());
      }
    }
    assets.splice(0, assets.length, ...assets.filter((asset) => named.has(asset.path.split("/").pop().toLowerCase())));
    if (!assets.length) return 0;
    const done = project.meta.shaders || {};
    const pending = [];
    for (const asset of assets) {
      const folder = asset.path.includes("/") ? asset.path.slice(0, asset.path.lastIndexOf("/") + 1) : "";
      const inputs = project.paths().filter((path) => path === asset.path ||
        (path.startsWith(folder) && !path.slice(folder.length).includes("/") && /\.(hlsl|hlsli|fx|h)$/i.test(path)));
      let key = "";
      for (const path of inputs) key += `${path}:${await hashBlob(project.blob(path))};`;
      if (done[asset.path] === key && project.has(`${asset.path}.wgsl-compiled`)) continue;
      pending.push({ ...asset, inputs, key });
    }
    if (!pending.length) return 0;
    if (!cloud || !cloud.translatesShaders) {
      write(`${pending.length} shader asset(s) need translating for the web, which this site's server cannot do; ` +
        "a program that loads one stops with an error that names it.", "note");
      return 0;
    }
    if (!signedIn()) {
      write(`Sign in to translate this program's ${pending.length} shader asset(s) for the web.`, "note");
      return 0;
    }
    let translated = 0;
    for (const asset of pending) {
      status(`Translating ${asset.path} for the web…`);
      const files = {};
      for (const path of asset.inputs) files[path] = new Uint8Array(await project.blob(path).arrayBuffer());
      try {
        const result = await cloud.translateShader(asset.path, files);
        if (!result.ok) {
          write(`Unable to translate ${asset.path} for the web:\n${result.output.trim()}`, "error");
          continue;
        }
        for (const [path, bytes] of Object.entries(result.files)) await project.write(path, bytes);
        done[asset.path] = asset.key;
        ++translated;
      } catch (error) {
        write(`Unable to translate ${asset.path} for the web: ${error.message}`, "error");
      }
    }
    await project.update({ shaders: done });
    if (translated) write(`Translated ${translated} shader asset(s) for the web.`, "note");
    return translated;
  }

  // A buddy session (buddies/panel.js) opens on the account's copy of the
  // project, so it is saved there first, with every change. Returns its id
  // in the account.
  async function prepareSession(project) {
    if (!cloud || !signedIn()) return null;
    await ide.saveAll();
    if (!project.meta.cloud) await saveToAccount(project);
    else await sync(project);
    if (!(await settled(project))) throw new Error(`${project.name} could not be saved to your account first`);
    return project.meta.cloud.id;
  }

  // After a buddy session the project saves to the account again, from the
  // revision the session saved last.
  async function resumeAfterSession(project) {
    if (!cloud || !project.meta.cloud || !signedIn()) return refresh();
    try {
      const remote = await cloud.getProject(project.meta.cloud.id);
      // The session's last save is the project now. Should this computer's
      // files differ from it, as when work went on here after the session
      // stopped saving, they are kept as a project of their own first.
      const local = await localState(project);
      const saved = Object.fromEntries(Object.entries(remote.files).map(([path, file]) => [path, file.hash]));
      if (!sameFiles(local.files, saved)) {
        const copy = await store.create({ name: `${project.name} (this computer)`,
          files: project.paths().map((path) => [path, project.blob(path)]), main: project.main, mode: project.meta.mode });
        notice(`${project.name} differed here from the buddy session's last save, so this computer's version is ` +
          `kept as ${copy.name}.`, [], "warning");
      }
      await pull(project, remote);
      await ide.reopen(project);
    } catch (error) {
      if (error instanceof CloudError && error.status === 404) await unlinked(project);
      else notice(`${project.name} could not be brought up to date from your account: ${error.message}`, [], "warning");
    }
    refresh();
  }

  // Whether the project's changes are all in the account.
  async function settled(project = ide.project()) {
    return !!(project && project.meta.cloud && !syncTimer && !syncing && project.meta.cloud.revision > 0 &&
      !(await changedSinceSync(project)));
  }

  refresh();
  return { refresh, changed, checkRemote, remoteOnly, openRemote, removeRemote, shareLink, openShare, prepareShaders,
    signedIn, settled, prepareSession, resumeAfterSession };
}
